import { CssBaseline } from '@material-ui/core'
import {
  CloudinaryProvider,
  GoogleMapsProvider,
  SnackbarProvider,
  ThemeProvider,
  UsersProvider,
  useUser,
  useClientOptions
} from '@paintscout/ui'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { LocationTrackerProvider as LocationTracker } from '@paintscout/react-quote'
import { motion, useAnimation } from 'framer-motion'
import React, { Suspense, useEffect } from 'react'
import { AuthProvider } from 'react-auth0'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import { ConnectionProvider } from 'react-detect-offline'
import type { RouteComponentProps } from 'react-router-dom'
import { Redirect, Router, Route as SimpleRoute, Switch, useLocation } from 'react-router-dom'
import { audience, auth, clearUserData, scope } from 'shared/util/auth'
import history from 'shared/util/routing/history'
import ErrorBoundary from 'src/components/common/ErrorBoundary'
import Header, { SideNav } from 'src/components/common/Header'
import 'src/util/appSentry'
import { getTheme } from 'src/util/theme'
import migrateOldKeys from '../../util/localstorage'
import RedirectHome from '../common/RedirectHome'
import Route from '../common/Route'
import TrialExpiredGuard from '../common/TrialExpiredGuard'
import ChurnedGuard from '../common/ChurnedGuard'
import CustomLogin from '../pages/CustomLogin'
import LoginCallback from '../pages/LoginCallback'
import Logout from '../pages/Logout'
import TrialOverDashboard from '../pages/dashboard/TrialOverDashboard'
import SherwinTrialOverDashboard from '../pages/dashboard/SherwinTrialOverDashboard'
import Dashboard from '../pages/dashboard/Dashboard/Dashboard'
import Locations from '../pages/locations/Locations'
import EditContact from '../pages/people/EditContact'

import EditDeal from '../pages/deals/EditDeal'
import CompanyList from '../pages/people/CompanyList'
import EditCompany from '../pages/people/EditCompany'
import PeopleList from '../pages/people/PeopleList'
import EditQuote from '../pages/quotes/EditQuote'
import NewQuote from '../pages/quotes/NewQuote/NewQuote'
import NewQuoteTemplate from '../pages/quotes/NewQuoteTemplate'
import QuoteList from '../pages/quotes/QuoteList'
import ReportRemoteView from '../pages/view/ReportRemoteView'
import ApiClientProvider from './ApiClientProvider'
import AuthorizeUser from './AuthorizeUser'
import CheckQuoteRecovery from './CheckQuoteRecovery'
import DialogStackProviderWithRouter from './DialogStackProviderWithRouter'
import LogRocket from './LogRocket'
import OnboardUser from './OnboardUser'
import ScrollToTop from './ScrollToTop'
import UpdateChecker from './UpdateChecker'
const IncomingIntegrationLink = React.lazy(() => import('../pages/integrate/IncomingIntegrationLink'))
const Profile = React.lazy(() => import('../pages/profile/Profile/Profile'))
const Settings = React.lazy(() => import('../pages/settings/Settings/Settings'))
const Reports = React.lazy(() => import('../pages/reports/Reports/Reports'))
const Update = React.lazy(() => import('../pages/Update'))

// For some reason, this was causing an error... I don't know why it would be different than the other lazy loaded components, though. Maybe because it gets hit before  the app has loaded?
// const Signup = React.lazy(() => import('../pages/Signup'))
import Signup from '../pages/Signup'
import Events from '../pages/events'
import Scheduling from '../pages/scheduling'
import Tasks from '../pages/tasks'
import DealView from '../pages/deals/DealView'
import UserFlow from './UserFlow'
import CRMTrialSync from './CRMTrialSync'
import HubspotChat from './HubspotChat'

const GenericReport = React.lazy(() => import('../pages/reports/Reports/GenericReport'))

// migrate old ls keys
migrateOldKeys()

const defaultTheme = getTheme()

function App() {
  const detectOfflineConfig = {
    url: `${process.env.PS_API_URL}/ps-ping`
  }

  return (
    <>
      {/* keep this as high up the tree as possible, lessens possibilty of an error causing this not to mount */}
      <UpdateChecker />

      <Router history={history}>
        <Switch>
          {/* 
          We put this route high up the tree so that there's little chance for an error from a parent component to break it.
          <SimpleRoute /> is just a regular react-router Route (our custom Route component will not work here)
        */}
          <SimpleRoute path="/update" exact component={Update} />
        </Switch>
        <ConnectionProvider config={detectOfflineConfig}>
          <CloudinaryProvider
            cloudName={process.env.CLOUDINARY_CLOUD_NAME}
            uploadPreset={process.env.CLOUDINARY_UPLOAD_PRESET}
            secure={true}
          >
            <ThemeProvider theme={defaultTheme}>
              <SnackbarProvider>
                <CssBaseline />
                <ScrollToTop />
                <ErrorBoundary isRootBoundary>
                  <LocationTracker>
                    <AuthProvider
                      auth0Client={auth}
                      scope={scope}
                      audience={audience}
                      onLogout={(companyId) => clearUserData(companyId)}
                      appUrl={process.env.PS_APP_URL}
                    >
                      <ApiClientProvider>
                        <DialogStackProviderWithRouter>
                          <Switch>
                            {/* Unauthenticated Routes */}
                            <Route path="/signup" exact component={Signup} />
                            <Route path="/login/:slug?" component={CustomLogin} />
                            <Route path="/logout" exact component={Logout} />
                            <Route path="/login-callback" exact component={LoginCallback} />

                            {/* Authenticated Routes */}
                            <Route
                              path="/"
                              render={() => (
                                <AuthorizeUser>
                                  <GoogleMapsProvider>
                                    <UsersProvider>
                                      <DialogStackProviderWithRouter>
                                        <OnboardUser />
                                        <UserFlow />
                                        <CheckQuoteRecovery />
                                        <LogRocket />
                                        <CRMTrialSync />
                                        <HubspotChat />
                                        <SideNav>
                                          <>
                                            <Header />
                                            <TrialExpiredGuard>
                                              <ChurnedGuard>
                                                <AnimatedSwitch>
                                                  <Route path="/dashboard" exact component={DashboardRoutes} auth />
                                                  <Route
                                                    path="/locations"
                                                    exact
                                                    component={Locations}
                                                    auth
                                                    roles={['corporate']}
                                                  />

                                                  <Route path="/deals" component={DealRoutes} auth />

                                                  <Route path="/quotes" component={QuoteRoutes} auth />
                                                  <Route
                                                    path="/invoices"
                                                    component={InvoiceRoutes}
                                                    auth
                                                    features={{ 'invoices.enabled': true }}
                                                  />
                                                  <Route path="/people" component={PeopleRoutes} auth />
                                                  <Route path="/profile/:category?" component={Profile} auth />
                                                  <Route
                                                    path="/settings/:section?"
                                                    component={Settings}
                                                    auth
                                                    permission={'write:account-settings'}
                                                  />

                                                  <Route
                                                    path="/integrate"
                                                    component={IntegrateRoutes}
                                                    auth
                                                    features={{ 'integrations.enabled': true }}
                                                  />

                                                  <Route path="/reports" component={ReportRoutes} auth />

                                                  <Route path="/view/:sendUrl/:silent?" component={ReportRemoteView} />

                                                  <Route path="/events" component={EventRoutes} auth />
                                                  <Route path="/tasks" component={TaskRoutes} auth />

                                                  <Route path="/scheduling" exact component={SchedulingRoutes} auth />
                                                  <Route path="/logout" exact component={Logout} auth />

                                                  <RedirectHome />
                                                </AnimatedSwitch>
                                              </ChurnedGuard>
                                            </TrialExpiredGuard>
                                          </>
                                        </SideNav>
                                      </DialogStackProviderWithRouter>
                                    </UsersProvider>
                                  </GoogleMapsProvider>
                                </AuthorizeUser>
                              )}
                            />
                          </Switch>
                        </DialogStackProviderWithRouter>
                      </ApiClientProvider>
                    </AuthProvider>
                  </LocationTracker>
                </ErrorBoundary>
              </SnackbarProvider>
            </ThemeProvider>
          </CloudinaryProvider>
        </ConnectionProvider>
      </Router>
    </>
  )
}

///////////////////////////
//        Routes        //
/////////////////////////

function QuoteRoutes({ match }: RouteComponentProps) {
  return (
    <Switch>
      <Route path={`${match.path}/new`} exact component={NewQuote} />
      <Route path={`${match.path}/edit/:id`} exact component={EditQuote} />
      <Route path={`${match.path}/new/template/:quoteType`} exact component={NewQuoteTemplate} />
      <Route path={`${match.path}/`} exact component={QuoteList} />
    </Switch>
  )
}

function DealRoutes({ match }: RouteComponentProps) {
  const { crmVisible } = useFlags()
  if (!crmVisible) {
    return <RedirectHome />
  }

  return (
    <Switch>
      <Route path={`${match.path}`} exact render={() => <Redirect to={`${match.path}/board`} />} />
      <Route path={`${match.path}/edit/:id`} exact component={EditDeal} />
      <Route path={`${match.path}/:view`} exact component={DealView} />
    </Switch>
  )
}

function EventRoutes({ match }: RouteComponentProps) {
  const { crmVisible } = useFlags()
  if (!crmVisible) {
    return <RedirectHome />
  }
  return (
    <Switch>
      <Route path={`${match.path}`} component={Events} />
    </Switch>
  )
}

function SchedulingRoutes({ match }: RouteComponentProps) {
  const { productionFlag } = useFlags()
  if (!productionFlag) {
    return <RedirectHome />
  }
  return (
    <Switch>
      <Route path={`${match.path}`} component={Scheduling} />
    </Switch>
  )
}

function TaskRoutes({ match }: RouteComponentProps) {
  const { crmVisible } = useFlags()
  if (!crmVisible) {
    return <RedirectHome />
  }
  return (
    <Switch>
      <Route path={`${match.path}`} exact component={Tasks} />
    </Switch>
  )
}

// invoices re-use the quote components, only difference is 'invoices' in URL
function InvoiceRoutes(props: RouteComponentProps) {
  // make sure we're mounting a different QuoteRoutes if we're switching between /quotes and /invoices
  return <QuoteRoutes {...props} />
}

function PeopleRoutes({ match }: RouteComponentProps) {
  return (
    <>
      <Route path={`${match.path}`} exact render={() => <Redirect to={`${match.path}/contacts`} />} />
      <Route path={`${match.path}/companies`} exact component={CompanyList} permission={'read:all-contacts'} />
      <Route path={`${match.path}/companies/:id`} exact component={EditCompany} permission={'read:all-contacts'} />
      <Route path={`${match.path}/contacts`} exact component={PeopleList} permission={'read:all-contacts'} />
      <Route path={`${match.path}/contact/:id`} exact component={EditContact} permission={'read:all-contacts'} />
    </>
  )
}

function DashboardRoutes(_: RouteComponentProps) {
  return <Route path="/dashboard" exact component={DashboardComponent} auth />
}

function DashboardComponent(_: RouteComponentProps) {
  const { isTrial, trialExpired, user } = useUser()
  const { sherwinWilliams } = useFlags()
  const leadSource = user?.app_metadata?.company?.leadSource || ''

  if (isTrial && trialExpired) {
    const isSherwinWilliams = sherwinWilliams || leadSource?.toLowerCase().includes('sherwin')
    if (isSherwinWilliams) {
      return <SherwinTrialOverDashboard />
    } else {
      return <TrialOverDashboard />
    }
  }

  return <Dashboard />
}

function IntegrateRoutes({ match }: RouteComponentProps) {
  return (
    <>
      <Route path={`${match.path}/:provider/:entity/`} exact component={IncomingIntegrationLink} />
    </>
  )
}

function ReportRoutes({ match }: RouteComponentProps) {
  const { crm: crmFlag } = useFlags()
  const { options } = useClientOptions()
  const reportsFeatureEnabled = options.options.features?.reports?.enabled

  if (!crmFlag && !reportsFeatureEnabled) {
    return <RedirectHome />
  }
  return (
    <>
      <Route path={`${match.path}`} exact component={Reports} />
      {/* 
        "Special" (ie: non-generic) reports can be put here.
        So far, we haven't needed that though.
        <Route path={`${match.path}/payments-collected`} component={PaymentsCollected} /> 
      */}
      <Route path={`${match.path}/:report`} component={GenericReport} />
    </>
  )
}

function AnimatedSwitch({ children }) {
  const location = useLocation()

  const controls = useAnimation()

  return (
    <Suspense
      fallback={
        // if the fallback is rendered, we know the page is being lazy loaded, so we know to trigger the fade-in
        <Mounter
          onMount={() => {
            // mount() is undocumented, not sure what it does other than it prevents an error about calling .set too soon
            controls.mount()

            controls.set({ opacity: 0 })
          }}
          onUnmount={() => {
            controls.start({ opacity: 1, transition: { duration: 0.3 } })
          }}
        />
      }
    >
      <motion.div animate={controls}>
        <Switch location={location}>{children}</Switch>
      </motion.div>
    </Suspense>
  )
}

function Mounter({ onMount, onUnmount }) {
  useEffect(() => {
    onMount()
    return () => onUnmount()
  }, [])
  return null
}

export default App
